import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Tag, Layers } from "lucide-react";
import { CASE_STUDIES } from "../data/mock";
import {
  EngagementModelsSection,
  FAQSection,
  IndustriesSection,
  TechnologiesSection,
  WhatWeDoSection,
} from "@/components/CommonSections";

const CaseStudiesPage = () => {
  // Get featured case study (Startup Review Ecosystem)
  const featuredStudy =
    CASE_STUDIES.find(
      (cs) => cs.slug === "ai-multi-agent-market-intelligence"
    ) || CASE_STUDIES[0];

  // Get all new case studies (IDs 8-35) for "More Success Stories"
  // const newCaseStudies = CASE_STUDIES.filter((cs) => cs.id >= 7 && cs.id <= 35);
  const ORDERED_IDS = [
    34, 35, 7, 8, 9, 15, 19, 16, 13, 24, 17, 12, 14, 10, 11, 18, 20, 21, 22, 23,
    25, 26, 27, 28, 29, 30, 31, 32,
  ];
  const newCaseStudies = ORDERED_IDS.map((id) =>
    CASE_STUDIES.find((cs) => cs.id === id)
  ).filter(Boolean);
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-red-300 font-semibold">Success Stories</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
              Case Studies
            </h1>
            <p className="text-xl text-gray-300">
              Explore how we've helped businesses across industries achieve
              their technology goals and drive measurable results.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Case Study Summary */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <span className="inline-flex items-center px-4 py-1.5 bg-[#b32126] text-white text-sm font-semibold rounded-full">
              Featured Project
            </span>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <div className="grid lg:grid-cols-2">
              {/* Image */}
              <div className="relative h-64 lg:h-auto overflow-hidden">
                <img
                  src={featuredStudy.heroImage || featuredStudy.image}
                  alt={featuredStudy.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent lg:hidden" />
              </div>

              {/* Content */}
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                {/* Tags */}
                <div className="flex flex-wrap gap-3 mb-4">
                  <Link
                    to={`/industries/${featuredStudy.industrySlug}`}
                    className="inline-flex items-center px-3 py-1.5 bg-red-50 text-[#b32126] text-sm font-medium rounded-full hover:bg-red-100 transition-colors"
                  >
                    <Tag className="h-4 w-4 mr-1.5" />
                    {featuredStudy.industry}
                  </Link>
                  <span className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                    <Clock className="h-4 w-4 mr-1.5" />
                    {featuredStudy.timeline}
                  </span>
                </div>

                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  {featuredStudy.title}
                </h2>
                <p className="text-lg text-[#b32126] font-medium mb-4">
                  {featuredStudy.tagline}
                </p>
                <p className="text-gray-600 mb-6">
                  {featuredStudy.challenge.substring(0, 200)}...
                </p>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6 py-4 border-y border-gray-100">
                  {featuredStudy.results.slice(0, 3).map((result, index) => (
                    <div key={index} className="text-center">
                      <div className="text-2xl font-bold text-[#b32126]">
                        {result.value}
                      </div>
                      <div className="text-xs text-gray-500">
                        {result.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Services */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {featuredStudy.services.map((service, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                    >
                      {service}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <Link
                  to={`/resources/case-studies/${featuredStudy.slug}`}
                  className="inline-flex items-center px-6 py-3 bg-[#b32126] text-white font-semibold rounded-xl hover:bg-[#8f1a1f] transition-colors w-fit"
                >
                  View Full Case Study
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Case Studies Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
            More Success Stories
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {newCaseStudies.map((study) => (
              <Link
                key={study.id}
                to={`/resources/case-studies/${study.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#b32126] text-white text-xs font-semibold rounded-full">
                      {study.industry}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-white/90 text-gray-700 text-xs font-medium rounded-full">
                      {study.timeline}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-2">
                    {study.clientType}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {study.tagline}
                  </p>

                  {/* Quick Results */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {study.results.slice(0, 2).map((result, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        {result.value} {result.label}
                      </span>
                    ))}
                  </div>

                  {/* Services */}
                  <div className="flex items-center text-sm text-gray-500">
                    <Layers className="h-4 w-4 mr-2" />
                    {study.services.slice(0, 2).join(", ")}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Common Sections */}
      <WhatWeDoSection />
      <IndustriesSection />
      <TechnologiesSection />
      <EngagementModelsSection bgColor="bg-white" />

      <FAQSection />

      {/* CTA Section */}
      <section className="py-16 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Be Our Next Success Story?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help you achieve similar results for your
            business.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#b32126] font-semibold rounded-xl hover:bg-gray-100 transition-colors shadow-lg"
          >
            Start Your Project
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
